import { type TFunction } from 'i18next';
import { matchIsValidTel } from 'mui-tel-input';
import * as yup from 'yup';

import { OrganizationDTO, OrganizationDTOTalentUniqueFieldsItem, TalentDTOType } from '@/api/generated';

export enum TalentModalEditSection {
  BASIC_INFO = 'BASIC_INFO',
  ADD_TO_PROCESS = 'ADD_TO_PROCESS',
  ADDITIONAL_INFO = 'ADDITIONAL_INFO'
}

export enum TalentBasicInfoViewType {
  EDIT = 'EDIT',
  CREATE = 'CREATE'
}

export const TalentModalKey = `editTalent` as const;

export function getAdditionalInfoKey(process: { id?: string }) {
  return `${TalentModalEditSection.ADDITIONAL_INFO}_${process.id}`;
}

export const getBasicInfoSchemaForEdit = (t: TFunction, organization: OrganizationDTO | undefined) => {
  let idNumber = yup.string();
  let passportNumber = yup.string();
  let phone = yup.string().test(`phone`, t('validation:phone'), value => !value || matchIsValidTel(value));

  if (organization?.talentUniqueFields?.includes(OrganizationDTOTalentUniqueFieldsItem.ID_NUMBER)) {
    idNumber = idNumber.required(
      t('validation:mandatory', { field: t('talents:layouts.talentEditModal.fields.idNumber') })
    );
  }

  if (organization?.talentUniqueFields?.includes(OrganizationDTOTalentUniqueFieldsItem.PASSPORT_NUMBER)) {
    passportNumber = passportNumber.required(
      t('validation:mandatory', { field: t('talents:layouts.talentEditModal.fields.passportNumber') })
    );
  }

  if (organization?.talentUniqueFields?.includes(OrganizationDTOTalentUniqueFieldsItem.PHONE))
    phone = phone.required(t('validation:mandatory', { field: t('talents:layouts.talentEditModal.fields.phone') }));

  return yup.object().shape({
    firstName: yup
      .string()
      .min(
        2,
        t('validation:limits.minimum', {
          field: t('talents:layouts.talentEditModal.fields.firstName'),
          count: 2,
          unit: t('validation:unit.character')
        })
      )
      .max(
        50,
        t('validation:limits.maximum', {
          field: t('talents:layouts.talentEditModal.fields.firstName'),
          count: 55,
          unit: t('validation:unit.characters')
        })
      )
      .required(t('validation:mandatory', { field: t('talents:layouts.talentEditModal.fields.firstName') })),
    lastName: yup
      .string()
      .min(
        2,
        t('validation:limits.minimum', {
          field: t('talents:layouts.talentEditModal.fields.lastName'),
          count: 2,
          unit: t('validation:unit.character')
        })
      )
      .max(
        50,
        t('validation:limits.maximum', {
          field: t('talents:layouts.talentEditModal.fields.lastName'),
          count: 55,
          unit: t('validation:unit.characters')
        })
      )
      .required(t('validation:mandatory', { field: t('talents:layouts.talentEditModal.fields.lastName') })),
    idNumber,
    passportNumber,
    phone,
    email: yup
      .string()
      .email(t('validation:email'))
      .required(t('validation:mandatory', { field: t('talents:layouts.talentEditModal.fields.email') })),
    type: yup.string().oneOf([TalentDTOType.EMPLOYEE, TalentDTOType.CANDIDATE]),
    organizationSlug: yup
      .string()
      .required(t('validation:mandatory', { field: t('talents:layouts.talentEditModal.fields.location') }))
  });
};

export const getBasicInfoSchemaForCreate = (t: TFunction, organization: OrganizationDTO | undefined) =>
  getBasicInfoSchemaForEdit(t, organization).concat(
    yup.object().shape({
      locations: yup
        .array()
        .of(
          yup
            .string()
            .required(t('validation:mandatory', { field: t('talents:layouts.talentEditModal.fields.location') }))
        )
        .min(1, t('validation:mandatory', { field: t('talents:layouts.talentEditModal.fields.location') }))
        .required(t('validation:mandatory', { field: t('talents:layouts.talentEditModal.fields.location') }))
    })
  );
